import React, { useState } from 'react'
import { Trash2 as Trash2Icon } from 'react-feather'
import { Controller, FormContext, useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'
import { KeyboardArrowUp as KeyboardArrowUpIcon } from '@material-ui/icons'
import { DatePicker } from '@material-ui/pickers'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'

import {
  Dai,
  FileUploadInput,
  Label,
  Permitted,
  Select,
  WhiteTooltip,
} from 'components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import schema from './schema'
import * as service from 'service'
import constants from 'constants/index'
import usePermissions from 'hooks/usePermissions'

const PlanActionForm = ({
  dataProcessTreatmentId,
  evidencePlanAction,
  planAction,
  isDisabled,
  refresh,
  users = [],
  isUsersLoading = false,
  onClose,
  setSelectedEvidencePlanAction,
}) => {
  const [loading, setLoading] = useState(false)

  const isConclusion =
    planAction?.status === constants.planAction.CONCLUSION_STATUS_ID

  const [openCollapse, setOpenCollapse] = useState(
    isConclusion || isDisabled ? false : true,
  )

  const snackbar = useSnackbar()
  const { some } = usePermissions()

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      what: planAction?.what ?? '',
      how: planAction?.how ?? '',
      because: planAction?.because ?? '',
      where: planAction?.where ?? '',
      howPrice: planAction?.howPrice ?? '',
      link: planAction?.link ?? '',
      startDate: planAction?.startDate ? new Date(planAction?.startDate) : null,
      deadlineDate: planAction?.deadlineDate
        ? new Date(planAction?.deadlineDate)
        : null,
      responsibleUserId: planAction?.responsible?.id ?? '',
      justify: evidencePlanAction?.evidence?.justify ?? '',
      evidences: evidencePlanAction?.evidence?.attachments,
    },
  })

  const { handleSubmit, control, errors } = formMethods

  const {
    CHIP_STATUS_COLORS,
    CHIP_STATUS_LABELS,
    CHIP_STATUS_TEXT_COLORS,
    HEADWAY_STATUS_ID,
    INITIAL_STATUS_ID,
    CONCLUSION_STATUS_ID,
    EVIDENCE_FILE_TYPES,
    DAI_SOURCE_ID,
  } = constants.planAction

  const baseTooltipTile =
    'Conteúdo gerado pela DAI com base na análise do processo'

  const daiSuggestion = planAction?.daiSuggestion
  const isDaiGenerated = planAction?.source === DAI_SOURCE_ID

  const isNew = !planAction?.id
  const formId = `data-process-treatment-form-${planAction?.id ?? 'new'}`

  const isPending = planAction?.status === INITIAL_STATUS_ID

  const canEdit = some([
    constants.permissions.DATA_PROCESS_TREATMENT.MANAGE,
    constants.permissions.DATA_PROCESSES.CREATE,
  ])

  const hiddenDaiIcon = (fieldName) =>
    !isDaiGenerated ||
    isEmpty(daiSuggestion?.[fieldName]) ||
    planAction?.[fieldName] !== daiSuggestion?.[fieldName]

  const handleChangeCollapse = () => setOpenCollapse((prevState) => !prevState)

  const handleOpenDeleteDialog = () =>
    setSelectedEvidencePlanAction(evidencePlanAction.id)

  const handleDeleteEvidenceDocument = async (fileId) => {
    await service.dponet.dataProcessTreatment.deleteEvidenceDocument({
      dataProcessTreatmentId,
      evidencePlanActionId: evidencePlanAction.id,
      documentId: fileId,
    })
  }

  const onSubmit = async (data, isConclusion) => {
    try {
      setLoading(true)

      const { startDate, deadlineDate, justify, evidences, ...rest } = data

      if (isNew) {
        await service.dponet.dataProcessTreatment.createEvidencePlanAction({
          dataProcessTreatmentId,
          evidence: {},
          planActions: [
            {
              ...rest,
              startDate: startDate?.toISOString(),
              deadlineDate: deadlineDate?.toISOString(),
              status: HEADWAY_STATUS_ID,
            },
          ],
        })
      } else {
        await service.dponet.dataProcessTreatment.updateEvidencePlanAction({
          dataProcessTreatmentId,
          evidencePlanActionId: evidencePlanAction?.id,
          evidence: {
            justify: justify,
            attachments: helpers.file.toArray(evidences),
          },
          planActions: [
            {
              ...rest,
              id: planAction?.id,
              startDate: startDate?.toISOString(),
              deadlineDate: deadlineDate?.toISOString(),
              status: isConclusion ? CONCLUSION_STATUS_ID : HEADWAY_STATUS_ID,
            },
          ],
        })
      }

      snackbar.open({
        message: `Plano de ação ${
          isConclusion ? 'finalizado' : isNew ? 'criado' : 'atualizado'
        } com sucesso!`,
        variant: 'success',
      })
      refresh()
      isNew && onClose()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Paper variant="outlined">
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gridGap={theme.spacing(2)}
          px={3}
          py={2}
        >
          <Box display="flex" gridGap={theme.spacing(2)} alignItems="center">
            <Typography variant="h5">
              {isNew
                ? 'Novo plano de ação'
                : `#${planAction?.id} Plano de ação`}
            </Typography>
            {!isNew && (
              <Chip
                size="small"
                label={CHIP_STATUS_LABELS[planAction?.status]}
                style={{
                  backgroundColor: CHIP_STATUS_COLORS[planAction?.status],
                  color: CHIP_STATUS_TEXT_COLORS[planAction?.status],
                }}
              />
            )}
          </Box>
          <Box display="flex" gridGap={theme.spacing(1)} alignItems="center">
            {isDaiGenerated && (
              <WhiteTooltip
                aiAlert
                cursor="help"
                title="Plano de ação gerado a partir da análise da DAI"
              >
                <InputAdornment position="start">
                  <Dai.StarIcon size={14} />
                </InputAdornment>
              </WhiteTooltip>
            )}
            <Permitted
              someTags={[
                constants.permissions.DATA_PROCESS_TREATMENT.MANAGE,
                constants.permissions.DATA_PROCESSES.CREATE,
              ]}
            >
              <IconButton
                size="small"
                onClick={isNew ? onClose : handleOpenDeleteDialog}
              >
                <Trash2Icon size={20} />
              </IconButton>
            </Permitted>
            <IconButton size="small" onClick={handleChangeCollapse}>
              <KeyboardArrowUpIcon
                style={{
                  transform: openCollapse ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.3s',
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <FormContext {...formMethods}>
        <Collapse in={openCollapse} timeout="auto" unmountOnExit>
          <Divider />
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            py={2}
            px={3}
            display="flex"
            flexDirection="column"
            gridGap={theme.spacing(3)}
            id={formId}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Label title="Responsável *" marginTopChildren={0}>
                  <Controller
                    as={
                      <Select
                        items={users}
                        error={!!errors.responsibleUserId}
                        helperText={errors?.responsibleUserId?.message}
                        fullWidth
                        disabled={isDisabled || !canEdit}
                        required
                        isLoading={isUsersLoading}
                      />
                    }
                    control={control}
                    name="responsibleUserId"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
              <Grid item xs={3}>
                <Label title="Início *" marginTopChildren={0}>
                  <Controller
                    as={
                      <DatePicker
                        fullWidth
                        format="DD/MM/yyyy"
                        error={!!errors?.startDate}
                        helperText={errors?.startDate?.message}
                        inputVariant="outlined"
                        onChange={(newValue) => ({ value: newValue })}
                        disabled={isDisabled || !canEdit}
                        required
                      />
                    }
                    control={control}
                    name="startDate"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
              <Grid item xs={3}>
                <Label title="Prazo final *" marginTopChildren={0}>
                  <Controller
                    as={
                      <DatePicker
                        fullWidth
                        format="DD/MM/yyyy"
                        helperText={errors?.deadlineDate?.message}
                        error={!!errors?.deadlineDate}
                        inputVariant="outlined"
                        onChange={(newValue) => ({ value: newValue })}
                        disabled={isDisabled || !canEdit}
                        required
                      />
                    }
                    control={control}
                    name="deadlineDate"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
              <Grid item xs={12}>
                <Label
                  title="O que"
                  description="Ações concretas e objetivas que serão tomadas"
                  marginTopChildren={0}
                >
                  <Controller
                    as={
                      <Dai.TextField
                        hiddenDaiIcon={hiddenDaiIcon('what')}
                        tooltipProps={{ title: baseTooltipTile }}
                        type="text"
                        color="primary"
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={10}
                        error={!!errors?.what}
                        helperText={errors?.what?.message}
                        fullWidth
                        disabled={isDisabled || !canEdit}
                      />
                    }
                    control={control}
                    name="what"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
              <Grid item xs={12}>
                <Label
                  title="Como"
                  description="Planejamento para execução da ação"
                  marginTopChildren={0}
                >
                  <Controller
                    as={
                      <Dai.TextField
                        hiddenDaiIcon={hiddenDaiIcon('how')}
                        tooltipProps={{ title: baseTooltipTile }}
                        type="text"
                        color="primary"
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={10}
                        error={!!errors?.how}
                        helperText={errors?.how?.message}
                        fullWidth
                        disabled={isDisabled || !canEdit}
                      />
                    }
                    control={control}
                    name="how"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
              <Grid item xs={12}>
                <Label
                  title="Porquê"
                  description="A meta que se objetiva com a ação"
                  marginTopChildren={0}
                >
                  <Controller
                    as={
                      <Dai.TextField
                        hiddenDaiIcon={hiddenDaiIcon('because')}
                        tooltipProps={{ title: baseTooltipTile }}
                        type="text"
                        color="primary"
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={10}
                        error={!!errors?.because}
                        helperText={errors?.because?.message}
                        fullWidth
                        disabled={isDisabled || !canEdit}
                      />
                    }
                    control={control}
                    name="because"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
              <Grid item xs={6}>
                <Label
                  title="Onde"
                  description="Em qual setor a ação será realizada"
                  marginTopChildren={0}
                >
                  <Controller
                    as={
                      <TextField
                        type="text"
                        color="primary"
                        variant="outlined"
                        error={!!errors?.where}
                        helperText={errors?.where?.message}
                        fullWidth
                        disabled={isDisabled || !canEdit}
                      />
                    }
                    control={control}
                    name="where"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
              <Grid item xs={6}>
                <Label
                  title="Quanto"
                  description="Impacto financeiro da ação"
                  marginTopChildren={0}
                >
                  <Controller
                    as={
                      <TextField
                        type="text"
                        color="primary"
                        variant="outlined"
                        error={!!errors?.howPrice}
                        helperText={errors?.howPrice?.message}
                        fullWidth
                        disabled={isDisabled || !canEdit}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography>R$</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    }
                    control={control}
                    name="howPrice"
                    mode="onChange"
                    onChange={([event]) => {
                      return helpers.formatters.currencyInput(
                        event.target.value || '0',
                      )
                    }}
                  />
                </Label>
              </Grid>
              <Grid item xs={12}>
                <Label
                  title="Link"
                  description="Upload de documento e/ou link"
                  marginTopChildren={0}
                >
                  <Controller
                    as={
                      <TextField
                        type="text"
                        color="primary"
                        variant="outlined"
                        error={!!errors?.link}
                        helperText={errors?.link?.message}
                        fullWidth
                        disabled={isDisabled || !canEdit}
                      />
                    }
                    control={control}
                    name="link"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
            </Grid>
            {!isNew && (
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(1)}
              >
                <Typography variant="h5">Evidência</Typography>
                <Label
                  title="O que foi feito"
                  description="Descreva a forma de implementação da evidência"
                  marginTopChildren={0}
                >
                  <Controller
                    as={
                      <TextField
                        type="text"
                        color="primary"
                        variant="outlined"
                        error={!!errors?.justify}
                        helperText={errors?.justify?.message}
                        fullWidth
                        disabled={isDisabled || !canEdit}
                        multiline
                        minRows={6}
                        maxRows={12}
                      />
                    }
                    control={control}
                    name="justify"
                  />
                </Label>
                <Controller
                  control={control}
                  name="evidences"
                  as={
                    <FileUploadInput
                      controlName="evidences"
                      accept={EVIDENCE_FILE_TYPES}
                      multiple
                      controllerBar
                      hasDownloadFile
                      disabled={isDisabled || !canEdit}
                      onRemoveApiFile={handleDeleteEvidenceDocument}
                    />
                  }
                />
              </Box>
            )}
            {!isDisabled && canEdit && (
              <Box
                display="flex"
                justifyContent="flex-end"
                gridGap={theme.spacing(2)}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={handleSubmit((data) => onSubmit(data, false))}
                >
                  Salvar
                </Button>
                {!isPending && !isNew && (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={handleSubmit((data) => onSubmit(data, true))}
                  >
                    Finalizar
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </FormContext>
    </Paper>
  )
}

export default PlanActionForm
